import { deleteObject, getBlob, getDownloadURL, ref, uploadBytes } from "firebase/storage";

const STRATS_PLANNER_MEDIA_ROOT = "strats-planner-media";
const STRATS_PLANNER_SHARED_LIBRARY_SCOPE = "shared";
const STRATS_PLANNER_LIBRARY_MANIFEST_NAME = "planner-library.json";
const STRATS_PLANNER_LIBRARY_SCHEMA_VERSION = 1;
const DEFAULT_CONTENT_TYPE = "image/png";

function sanitizePathSegment(value, fallback = "item") {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || fallback;
}

function sanitizeOwnerUidSegment(value, fallback = "anonymous") {
  const normalized = String(value || "")
    .trim()
    .replace(/[\\/]+/g, "-");

  return normalized || fallback;
}

function getFileExtension(fileName, fallback = ".png") {
  const match = /\.([a-z0-9]+)$/i.exec(String(fileName || "").trim());
  return match ? `.${match[1].toLowerCase()}` : fallback;
}

function buildVersionedFileName(fileName, versionTag) {
  const extension = getFileExtension(fileName);
  const stem = String(fileName || "image")
    .replace(/\.[^.]+$/, "")
    .trim();

  return `${sanitizePathSegment(stem, "image")}.${versionTag}${extension}`;
}

export function buildStratsPlannerMediaPath({
  ownerUid,
  mediaType,
  libraryKey,
  variantKey = null,
  fileName,
  versionTag = Date.now().toString(36),
}) {
  const safeOwnerUid = sanitizeOwnerUidSegment(ownerUid, "anonymous");
  const safeMediaType = sanitizePathSegment(mediaType, "misc");
  const safeLibraryKey = sanitizePathSegment(libraryKey, "library-item");
  const versionedFileName = buildVersionedFileName(fileName, versionTag);
  const pathSegments = [
    STRATS_PLANNER_MEDIA_ROOT,
    safeMediaType,
    safeOwnerUid,
    safeLibraryKey,
  ];

  if (variantKey) {
    pathSegments.push(sanitizePathSegment(variantKey, "variant"));
  }

  pathSegments.push(versionedFileName);
  return pathSegments.join("/");
}

export async function uploadStratsPlannerMedia({
  storage,
  ownerUid,
  mediaType,
  libraryKey,
  variantKey = null,
  file,
  blob = null,
  fileName = "",
  contentType = "",
  customMetadata = {},
}) {
  if (!storage) {
    throw new Error("Firebase Storage is unavailable.");
  }

  if (!ownerUid) {
    throw new Error("A signed-in user is required before planner media can upload to Firebase Storage.");
  }

  const payload = file || blob;
  if (!payload) {
    throw new Error("No planner media payload was provided.");
  }

  const resolvedFileName = fileName || file?.name || "image.png";
  const resolvedContentType = contentType || file?.type || payload?.type || DEFAULT_CONTENT_TYPE;
  const storagePath = buildStratsPlannerMediaPath({
    ownerUid,
    mediaType,
    libraryKey,
    variantKey,
    fileName: resolvedFileName,
  });
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, payload, {
    cacheControl: "public, max-age=31536000, immutable",
    contentDisposition: `inline; filename="${resolvedFileName.replace(/"/g, "")}"`,
    contentType: resolvedContentType,
    customMetadata: {
      plannerMediaRoot: STRATS_PLANNER_MEDIA_ROOT,
      libraryKey: String(libraryKey || ""),
      variantKey: variantKey ? String(variantKey) : "",
      mediaType: String(mediaType || ""),
      ownerUid: String(ownerUid || ""),
      ...customMetadata,
    },
  });

  const downloadUrl = await getDownloadURL(storageRef);

  return {
    src: downloadUrl,
    downloadUrl,
    storagePath,
    ownerUid,
    mediaType,
    libraryKey,
    variantKey: variantKey || null,
  };
}

export async function deleteStratsPlannerMedia({ storage, storagePath, ownerUid, currentUid }) {
  if (!storage || !storagePath || !ownerUid || !currentUid || ownerUid !== currentUid) {
    return false;
  }

  try {
    await deleteObject(ref(storage, storagePath));
    return true;
  } catch (error) {
    console.warn("Strats planner: failed to delete prior planner media object", error);
    return false;
  }
}

export function dataUrlToBlob(dataUrl) {
  if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) {
    return null;
  }

  const [metadata, base64Payload = ""] = dataUrl.split(",");
  const mimeTypeMatch = /^data:([^;]+);base64$/i.exec(metadata);
  const mimeType = mimeTypeMatch?.[1] || DEFAULT_CONTENT_TYPE;
  const binary = window.atob(base64Payload);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
}

export function buildStratsPlannerMediaLibraryPath(scope = STRATS_PLANNER_SHARED_LIBRARY_SCOPE) {
  return [
    STRATS_PLANNER_MEDIA_ROOT,
    "library",
    sanitizePathSegment(scope, STRATS_PLANNER_SHARED_LIBRARY_SCOPE),
    STRATS_PLANNER_LIBRARY_MANIFEST_NAME,
  ].join("/");
}

export async function loadSharedStratsPlannerMediaLibrary({
  storage,
  scope = STRATS_PLANNER_SHARED_LIBRARY_SCOPE,
}) {
  if (!storage) {
    return null;
  }

  const storagePath = buildStratsPlannerMediaLibraryPath(scope);

  try {
    const manifestBlob = await getBlob(ref(storage, storagePath));
    const manifestText = await manifestBlob.text();
    const parsed = JSON.parse(manifestText);
    return parsed?.library || null;
  } catch (error) {
    const code = error?.code || "";
    if (
      code === "storage/object-not-found" ||
      code === "storage/unknown" ||
      code === 404
    ) {
      return null;
    }
    throw error;
  }
}

export async function saveSharedStratsPlannerMediaLibrary({
  storage,
  library,
  scope = STRATS_PLANNER_SHARED_LIBRARY_SCOPE,
  updatedByUid = "",
}) {
  if (!storage) {
    throw new Error("Firebase Storage is unavailable.");
  }

  const storagePath = buildStratsPlannerMediaLibraryPath(scope);
  const manifestPayload = {
    schemaVersion: STRATS_PLANNER_LIBRARY_SCHEMA_VERSION,
    scope,
    updatedAt: new Date().toISOString(),
    updatedByUid: String(updatedByUid || ""),
    library: library || { maps: [], assets: [] },
  };
  const manifestBlob = new Blob([JSON.stringify(manifestPayload, null, 2)], {
    type: "application/json",
  });
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, manifestBlob, {
    cacheControl: "no-cache, no-store, max-age=0, must-revalidate",
    contentDisposition: `inline; filename="${STRATS_PLANNER_LIBRARY_MANIFEST_NAME}"`,
    contentType: "application/json",
    customMetadata: {
      plannerMediaRoot: STRATS_PLANNER_MEDIA_ROOT,
      plannerLibraryScope: String(scope || STRATS_PLANNER_SHARED_LIBRARY_SCOPE),
      updatedByUid: String(updatedByUid || ""),
    },
  });

  return {
    storagePath,
    downloadUrl: await getDownloadURL(storageRef),
    manifest: manifestPayload,
  };
}

export {
  STRATS_PLANNER_MEDIA_ROOT,
  STRATS_PLANNER_SHARED_LIBRARY_SCOPE,
};
